import React, { useEffect, useState } from "react";
import { Heart, Wind, Anchor, RefreshCcw, BookOpen, Sparkles, Flame } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { levelFor } from "../lib/levels";
import { SharedPreviewStyles, PEACH } from "../components/RepDetailModal";

/* ──────────────────────────────────────────────────────────────────────
 *  Sessions page
 *  Reads /api/sessions/summary and renders 5 stage-tinted cards mirroring
 *  the landing-page "daily rep" previews — same chart language, same 2.4s
 *  family heartbeat, but with the user's real session data and the active
 *  stage palette (accent, text, surface).
 * ────────────────────────────────────────────────────────────────────── */

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const shortDow = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return DOW[d.getDay()];
};

/* ─── Mood ─────────────────────────────────────────────────────────── */
const MoodCard = ({ data, lvl }) => {
  // Fill missing days with 0 so the chart still draws a baseline path,
  // but track which days are real so we only render markers for them.
  const days = data.series.map((d) => ({
    dow: shortDow(d.date),
    value: d.value,           // null if no log
    plot: d.value ?? 5,       // value used for path drawing
    has: d.value != null,
  }));
  const real = days.filter((d) => d.has);
  const avg = real.length ? (real.reduce((s, x) => s + x.value, 0) / real.length).toFixed(1) : "—";
  const lift = real.length >= 2 ? (real[real.length - 1].value - real[0].value).toFixed(1) : null;
  const peak = real.length ? real.reduce((a, b) => (b.value > a.value ? b : a)) : null;

  const W = 600, H = 210, padL = 38, padR = 14, padT = 30, padB = 30;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const stepX = innerW / (days.length - 1);
  const xFor = (i) => padL + i * stepX;
  const yFor = (v) => padT + innerH - (v / 10) * innerH;
  const points = days.map((d, i) => [xFor(i), yFor(d.plot)]);
  const smoothPath = (pts) => {
    if (pts.length < 2) return "";
    const path = [`M ${pts[0][0]} ${pts[0][1]}`];
    for (let i = 0; i < pts.length - 1; i++) {
      const [x1, y1] = pts[i];
      const [x2, y2] = pts[i + 1];
      const cpx = (x1 + x2) / 2;
      path.push(`C ${cpx} ${y1}, ${cpx} ${y2}, ${x2} ${y2}`);
    }
    return path.join(" ");
  };
  const linePath = smoothPath(points);
  const baseY = padT + innerH;
  const areaPath = `${linePath} L ${xFor(days.length - 1)} ${baseY} L ${padL} ${baseY} Z`;
  const avgY = real.length ? yFor(parseFloat(avg)) : baseY;

  return (
    <div className="rounded-2xl p-5" style={{ backgroundColor: lvl.surface, border: `1px solid ${lvl.text}10` }}>
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] opacity-60">7-day mood</div>
          <div className="font-display font-black text-2xl mt-1 leading-none ml-stat-pop" style={{ animationDelay: "0ms" }}>
            {real.length === 0 ? "Begin your story" : lift !== null && parseFloat(lift) > 0 ? <>Trending up <span style={{ color: lvl.accent }}>↑</span></> : lift !== null && parseFloat(lift) < 0 ? <>Honest week <span style={{ color: PEACH }}>↓</span></> : "Steady"}
          </div>
        </div>
        <div className="flex gap-2">
          <div className="rounded-xl px-3 py-2 text-center ml-stat-pop" style={{ backgroundColor: `${lvl.text}08`, animationDelay: "320ms" }}>
            <div className="text-[9px] uppercase tracking-widest opacity-50 font-bold">Avg</div>
            <div className="font-display font-black text-lg leading-none mt-0.5">{avg}</div>
          </div>
          <div className="rounded-xl px-3 py-2 text-center ml-stat-pop" style={{ backgroundColor: `${lvl.accent}66`, animationDelay: "640ms" }}>
            <div className="text-[9px] uppercase tracking-widest opacity-60 font-bold">Logs</div>
            <div className="font-display font-black text-lg leading-none mt-0.5" style={{ color: lvl.text }}>{real.length}</div>
          </div>
          <div className="rounded-xl px-3 py-2 text-center ml-stat-pop" style={{ backgroundColor: `${lvl.text}08`, animationDelay: "960ms" }}>
            <div className="text-[9px] uppercase tracking-widest opacity-50 font-bold">Peak</div>
            <div className="font-display font-black text-lg leading-none mt-0.5">{peak ? peak.dow : "—"}</div>
          </div>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: "auto", display: "block" }} role="img" aria-label="7-day mood line chart">
        <defs>
          <linearGradient id="moodAreaSession" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lvl.accent} stopOpacity="0.55" />
            <stop offset="100%" stopColor={lvl.accent} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0, 2.5, 5, 7.5, 10].map((g) => (
          <g key={g}>
            <line x1={padL} x2={W - padR} y1={yFor(g)} y2={yFor(g)} stroke={lvl.text} strokeOpacity="0.08" strokeDasharray="2 4" />
            <text x={padL - 8} y={yFor(g) + 3} fontSize="9" textAnchor="end" fill={lvl.text} fillOpacity="0.4" fontWeight="700">{g}</text>
          </g>
        ))}
        {days.map((d, i) => (
          <line key={`v-${i}`} x1={xFor(i)} x2={xFor(i)} y1={padT} y2={baseY} stroke={lvl.text} strokeOpacity="0.04" />
        ))}

        {real.length >= 2 && <path d={areaPath} fill="url(#moodAreaSession)" className="ml-mood-area" />}
        {real.length >= 2 && (
          <path d={linePath} pathLength="1" fill="none" stroke={lvl.text} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ml-mood-line" />
        )}

        {real.length >= 1 && (
          <g className="ml-mood-avgline">
            <line x1={padL} x2={W - padR} y1={avgY} y2={avgY} stroke={lvl.text} strokeOpacity="0.35" strokeDasharray="4 4" strokeWidth="1" />
            <text x={W - padR} y={avgY - 5} fontSize="9" textAnchor="end" fill={lvl.text} fillOpacity="0.55" fontWeight="700">avg {avg}</text>
          </g>
        )}

        {days.map((d, i) => {
          const [x, y] = points[i];
          if (!d.has) {
            // ghost marker for missing days
            return (
              <g key={d.dow + i}>
                <text x={x} y={baseY + 18} fontSize="11" textAnchor="middle" fill={lvl.text} fillOpacity="0.35" fontWeight="700">{d.dow}</text>
              </g>
            );
          }
          const isPeak = peak && d.value === peak.value && d.dow === peak.dow;
          return (
            <g key={d.dow + i} className="ml-mood-marker" style={{ animationDelay: `${1.0 + i * 0.22}s` }}>
              {isPeak && <circle className="ml-mood-peak-halo" cx={x} cy={y} r="14" fill={lvl.accent} fillOpacity="0.45" />}
              <circle className={isPeak ? "ml-mood-peak-core" : undefined} cx={x} cy={y} r={isPeak ? 8 : 6} fill={lvl.accent} stroke={lvl.surface} strokeWidth="2.5" />
              <text x={x} y={y - 14} fontSize="10" textAnchor="middle" fill={lvl.text} fillOpacity="0.75" fontWeight="700">{d.value.toFixed(1)}</text>
              <text x={x} y={baseY + 18} fontSize="11" textAnchor="middle" fill={lvl.text} fillOpacity={isPeak ? 0.9 : 0.55} fontWeight="700">{d.dow}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/* ─── Breathing ────────────────────────────────────────────────────── */
const BreathingCard = ({ data, lvl }) => (
  <div className="rounded-2xl p-5" style={{ backgroundColor: lvl.surface, border: `1px solid ${lvl.text}10` }}>
    <style>{`
      @keyframes mlBreathTraceLocal { 0% { stroke-dashoffset: 272; } 50% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -272; } }
      @keyframes mlBreathHaloLocal { 0%,100% { transform: scale(1); opacity: 0.35; } 50% { transform: scale(1.14); opacity: 0.65; } }
      @keyframes mlBreathCountLocal { 0%,100% { transform: scale(1); opacity: 0.9; } 50% { transform: scale(1.08); opacity: 1; } }
      .ml-bsx-trace { animation: mlBreathTraceLocal 8s linear infinite; }
      .ml-bsx-halo  { animation: mlBreathHaloLocal 4s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
      .ml-bsx-count { animation: mlBreathCountLocal 4s ease-in-out infinite; }
      @media (prefers-reduced-motion: reduce) {
        .ml-bsx-trace, .ml-bsx-halo, .ml-bsx-count { animation: none !important; }
      }
    `}</style>
    <div className="flex items-center justify-between mb-4">
      <span className="text-[11px] font-bold uppercase tracking-[0.22em] opacity-60">Box 4·4·4·4</span>
      <span className="text-[11px] font-bold uppercase tracking-[0.22em] ml-stat-pop" style={{ color: lvl.accent, animationDelay: "320ms" }}>Lifetime</span>
    </div>
    <div className="flex items-center gap-5">
      <div className="relative flex-shrink-0" style={{ width: 120, height: 120 }}>
        <svg width="120" height="120" viewBox="0 0 96 96" aria-hidden="true">
          <circle className="ml-bsx-halo" cx="48" cy="48" r="42" fill={`${lvl.accent}55`} />
          <rect x="14" y="14" width="68" height="68" rx="14" fill="none" stroke={lvl.text} strokeOpacity="0.12" strokeWidth="6" />
          <rect className="ml-bsx-trace" x="14" y="14" width="68" height="68" rx="14" fill="none" stroke={lvl.accent} strokeWidth="6" strokeDasharray="272" strokeLinecap="round" transform="rotate(-90 48 48)" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="ml-bsx-count font-display font-black" style={{ color: lvl.text, fontSize: "2rem", lineHeight: 1, display: "inline-block", transformOrigin: "center" }}>
            {data.total}
          </span>
        </div>
      </div>
      <div className="flex-1 space-y-2">
        <div className="flex justify-between ml-stat-pop" style={{ animationDelay: "320ms" }}>
          <span className="text-sm opacity-70">Total sessions</span>
          <span className="font-bold text-sm">{data.total}</span>
        </div>
        <div className="flex justify-between ml-stat-pop" style={{ animationDelay: "640ms" }}>
          <span className="text-sm opacity-70">This week</span>
          <span className="font-bold text-sm">{data.week_dates.length} / 7 days</span>
        </div>
        <div className="flex justify-between ml-stat-pop" style={{ animationDelay: "960ms" }}>
          <span className="text-sm opacity-70">Est. minutes</span>
          <span className="font-bold text-sm" style={{ color: lvl.accent }}>{data.total * 4} min</span>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Grounding ────────────────────────────────────────────────────── */
const GroundingCard = ({ data, lvl }) => {
  const weekSet = new Set(data.week_dates);
  // Build 7-day grid (oldest → newest) showing which days had a session
  const today = new Date();
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (6 - i));
    const iso = d.toISOString().slice(0, 10);
    return { dow: DOW[d.getDay()], iso, done: weekSet.has(iso) };
  });

  return (
    <div className="rounded-2xl p-5" style={{ backgroundColor: lvl.surface, border: `1px solid ${lvl.text}10` }}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] opacity-60">5-4-3-2-1 anchor</span>
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] ml-stat-pop" style={{ color: lvl.accent, animationDelay: "320ms" }}>{data.week_dates.length}/7 this week</span>
      </div>
      <div className="space-y-2.5">
        {days.map((d, idx) => {
          const phaseDelay = `${-idx * 0.6}s`;
          return (
            <div key={d.iso} className="flex items-center gap-3 py-1.5">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-display font-black text-sm flex-shrink-0 ${!d.done ? "ml-ground-pulse" : ""}`}
                style={{ backgroundColor: d.done ? lvl.accent : PEACH, color: lvl.text }}
              >
                {d.dow.charAt(0)}
              </div>
              <span className="font-semibold" style={{ opacity: d.done ? 1 : 0.65 }}>{d.dow}</span>
              {d.done && (
                <>
                  <div className="flex-1 min-w-0 mx-2 self-stretch flex items-center" aria-hidden="true">
                    <svg viewBox="0 0 100 20" preserveAspectRatio="none" style={{ width: "100%", height: 20, display: "block" }}>
                      <path d="M 0,10 L 30,10 L 34,12 L 38,4 L 42,16 L 46,10 L 100,10" fill="none" stroke={`${lvl.text}14`} strokeWidth="1" />
                      <path d="M 0,10 L 30,10 L 34,12 L 38,4 L 42,16 L 46,10 L 100,10" pathLength="100" fill="none" stroke={PEACH} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="ml-ecg-trace" style={{ animationDelay: phaseDelay }} />
                    </svg>
                  </div>
                  <span className="ml-ground-check text-sm font-bold uppercase tracking-wider flex-shrink-0" style={{ color: lvl.accent, animationDelay: phaseDelay }}>✓</span>
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center justify-between text-xs">
        <span className="opacity-60 font-semibold">Lifetime sessions</span>
        <span className="font-bold">{data.total}</span>
      </div>
    </div>
  );
};

/* ─── CBT Reframes ─────────────────────────────────────────────────── */
const ReframeCard = ({ data, lvl }) => {
  const latest = data.latest;
  return (
    <div className="rounded-2xl p-5" style={{ backgroundColor: lvl.surface, border: `1px solid ${lvl.text}10` }}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] opacity-60">{data.week} this week · {data.total} lifetime</span>
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] ml-stat-pop" style={{ color: lvl.accent, animationDelay: "320ms" }}>cognitive flex</span>
      </div>
      {latest ? (
        <div className="space-y-3">
          <div className="rounded-xl p-3" style={{ backgroundColor: `${lvl.text}06` }}>
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-1">Latest hot thought</div>
            <div className="text-sm italic">"{latest.thought}"</div>
          </div>
          <div className="flex flex-col items-center" aria-hidden="true">
            <svg viewBox="0 0 24 60" preserveAspectRatio="none" style={{ width: 24, height: 60, display: "block" }}>
              <path d="M 12,0 L 12,18 L 9,20 L 18,25 L 4,30 L 12,33 L 12,60" fill="none" stroke={`${lvl.text}14`} strokeWidth="1" />
              <path d="M 12,0 L 12,18 L 9,20 L 18,25 L 4,30 L 12,33 L 12,60" pathLength="100" fill="none" stroke={PEACH} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="ml-ecg-trace" />
            </svg>
            <div className="text-[9px] font-bold uppercase tracking-[0.28em] opacity-55 mt-1" style={{ color: lvl.text }}>rewire</div>
          </div>
          <div className="rounded-xl p-3 ml-cooler-bloom" style={{ backgroundColor: `${lvl.accent}55`, border: `1px solid ${lvl.accent}` }}>
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-1">Cooler thought</div>
            <div className="text-sm">"{latest.reframe}"</div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl p-6 text-center" style={{ backgroundColor: `${PEACH}1f`, border: `1px solid ${PEACH}66` }}>
          <div className="text-sm font-bold mb-1">No reframes logged yet</div>
          <div className="text-xs opacity-70">Catch one hot thought today — your cooler thought is waiting.</div>
        </div>
      )}
    </div>
  );
};

/* ─── Journal ──────────────────────────────────────────────────────── */
const JournalCard = ({ data, lvl, streak }) => {
  const latest = data.latest;
  return (
    <div className="rounded-2xl p-5" style={{ backgroundColor: lvl.surface, border: `1px solid ${lvl.text}10` }}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] opacity-60">{data.total} entries lifetime</span>
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] ml-stat-pop flex items-center gap-1" style={{ color: lvl.accent, animationDelay: "320ms" }}>
          <Flame size={12} /> {streak}-day streak
        </span>
      </div>
      {latest ? (
        <>
          <p className="text-sm leading-relaxed" style={{ color: `${lvl.text}cc` }}>
            {(latest.text || "").length > 280 ? (latest.text || "").slice(0, 280) + "…" : latest.text}
          </p>
          <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t" style={{ borderColor: `${lvl.text}10` }}>
            <span className="text-xs opacity-60 font-semibold flex-shrink-0">{data.latest_word_count} words</span>
            <div className="flex-1 min-w-0 mx-2 self-stretch flex items-center" aria-hidden="true">
              <svg viewBox="0 0 100 20" preserveAspectRatio="none" style={{ width: "100%", height: 20, display: "block" }}>
                <path d="M 0,10 L 30,10 L 34,12 L 38,4 L 42,16 L 46,10 L 100,10" fill="none" stroke={`${lvl.text}14`} strokeWidth="1" />
                <path d="M 0,10 L 30,10 L 34,12 L 38,4 L 42,16 L 46,10 L 100,10" pathLength="100" fill="none" stroke={PEACH} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="ml-ecg-trace" />
              </svg>
            </div>
            <span className="ml-private-stamp text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 flex-shrink-0" style={{ color: lvl.text }}>kept private</span>
          </div>
        </>
      ) : (
        <div className="rounded-xl p-6 text-center" style={{ backgroundColor: `${PEACH}1f`, border: `1px solid ${PEACH}66` }}>
          <div className="text-sm font-bold mb-1">Your page is blank</div>
          <div className="text-xs opacity-70">One honest line tonight will start the streak.</div>
        </div>
      )}
    </div>
  );
};

/* ─── Page ─────────────────────────────────────────────────────────── */
const CARD_HEADERS = [
  { key: "mood",      icon: Heart,     title: "Mood check-in",      sub: "Your inner weather, mapped." },
  { key: "breathing", icon: Wind,      title: "Box breathing",      sub: "Every breath you've trained." },
  { key: "grounding", icon: Anchor,    title: "5-4-3-2-1 grounding", sub: "Every time you came back." },
  { key: "cbt",       icon: RefreshCcw, title: "CBT reframes",       sub: "The thoughts you rewired." },
  { key: "journal",   icon: BookOpen,  title: "Journal",            sub: "The pages you've kept." },
];

export default function Sessions() {
  const { user } = useAuth();
  const lvl = levelFor(user?.current_level || 1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/sessions/summary")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load sessions."));
  }, []);

  return (
    <div className="animate-fadeUp" data-testid="sessions-page">
      <SharedPreviewStyles />

      <div className="flex items-center gap-3">
        <div className="rounded-2xl flex items-center justify-center" style={{ backgroundColor: lvl.accent, color: lvl.accentOn || lvl.text, width: 56, height: 56 }}>
          <Sparkles size={26} strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] opacity-60">Stage {lvl.level} · {lvl.aesthetic}</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Your sessions</h1>
        </div>
      </div>
      <p className="opacity-70 mt-3 max-w-2xl">A live report from your training — chart by chart, rep by rep. The story isn't tidy; it's yours.</p>

      {err && (
        <div className="mt-6 rounded-2xl p-4 text-sm" style={{ backgroundColor: `${PEACH}1f`, border: `1px solid ${PEACH}66` }} data-testid="sessions-error">
          {err}
        </div>
      )}

      {!data && !err && (
        <div className="mt-8 opacity-60 text-sm">Loading your data…</div>
      )}

      {data && (
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {CARD_HEADERS.map((h) => {
            const Icon = h.icon;
            const slice = data[h.key];
            return (
              <section key={h.key} data-testid={`session-card-${h.key}`} className="rounded-3xl p-6 sm:p-7 shadow-lg" style={{ backgroundColor: lvl.background === lvl.surface ? `${lvl.text}08` : lvl.surface, border: `1px solid ${lvl.text}10` }}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: lvl.accent, color: lvl.accentOn || lvl.text, width: 44, height: 44 }}>
                    <Icon size={22} strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="font-display font-black text-xl leading-tight">{h.title}</div>
                    <div className="text-xs opacity-60 font-semibold">{h.sub}</div>
                  </div>
                </div>
                {h.key === "mood"      && <MoodCard      data={slice} lvl={lvl} />}
                {h.key === "breathing" && <BreathingCard data={slice} lvl={lvl} />}
                {h.key === "grounding" && <GroundingCard data={slice} lvl={lvl} />}
                {h.key === "cbt"       && <ReframeCard   data={slice} lvl={lvl} />}
                {h.key === "journal"   && <JournalCard   data={slice} lvl={lvl} streak={data.user.streak} />}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
